import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {UtilsService} from "@shared-services/utils.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {TabManagementService} from "@core-services/tab-management.service";
import {PaginatorService} from "@shared-services/paginator.service";
import {SurveyService} from "../../../survey.service";
import {ConsoleContextService} from "@security/console-context.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {MatDialog} from "@angular/material/dialog";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {AudienceCategory, Survey, SurveyStatus, UpdateSurveyRequest} from "../../../surveys.model";
import {ConfirmationDialogComponent} from "@ui-kit/webkit/components/confirmation-dialog/confirmation-dialog.component";
import {displayUuid} from "@ui-kit/framework/util/static/string-format-util";
import {toIsoDateEndOfDay, toIsoDateStartOfDay} from "@ui-kit/framework/util/static/date-utils";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-survey-details',
  templateUrl: './survey-details.component.html'
})
export class SurveyDetailsComponent extends BasePageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() survey!: Survey;
  @Output() updatedSurvey: EventEmitter<Survey> = new EventEmitter<Survey>();

  @Input() pendingChanges: boolean = false;
  @Output() pendingChangesChange = new EventEmitter<boolean>();

  public loading$ = this.loadingService.loading$;
  private _clearOnDestroy$ = new Subject<void>();
  public surveyForm!: FormGroup;

  protected readonly AudienceCategory = AudienceCategory;
  protected readonly SurveyStatus = SurveyStatus;
  protected readonly displayUuid = displayUuid;

  constructor(injector: Injector,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private paginatorService: PaginatorService,
              private surveyService: SurveyService,
              private context: ConsoleContextService,
              public loader: LoadingService,
              private matDialog: MatDialog,
              private fb: FormBuilder,
              protected loadingService: LoadingService,
              private snackbarService: SnackbarService,
              private dialog: MatDialog) {
    super(injector);
  }

  ngOnInit(): void {
    this.surveyForm = this.fb.group({
      name: [this.survey.name, [Validators.required, Validators.pattern(/^[A-Za-z0-9 ._-]+$/)]],
      displayTitle: [this.survey.displayTitle, Validators.required],
      audience: [this.survey.audience, Validators.required],
      startDate: [new Date(this.survey.startDate!), Validators.required],
      endDate: [this.survey.endDate ? new Date(this.survey.endDate) : null]
    }, { validators: this.startDateBeforeEndDate });
    this.pendingChanges = false;

    this.surveyForm.valueChanges
      .pipe(takeUntil(this._clearOnDestroy$))
      .subscribe(() => {
        if(this.surveyForm.dirty) {
          this.markPendingChanges(true);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['survey']) {
      this.ngOnInit();
    }
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  markPendingChanges(value: boolean): void {
    this.pendingChanges = value;
    this.pendingChangesChange.emit(value);
  }

  startDateBeforeEndDate(group: AbstractControl): ValidationErrors | null {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return startDate <= endDate ? null : { dateOrder: 'Start date must be before end date' };
    }
    return null;
  }

  onSubmit(): void {
    if (this.surveyForm.valid) {
      const formValue = this.surveyForm.value;

      const updateSurveyRequest = {
        ...this.survey,
        ...formValue,
        startDate: toIsoDateStartOfDay(formValue.startDate),
        endDate: toIsoDateEndOfDay(formValue.endDate)
      }

      this.surveyService.updateSurvey(updateSurveyRequest)
        .pipe(this.loadingService.withLoader())
        .subscribe({
          next: (updated: Survey) => {
            this.survey = updated;
            this.surveyForm.patchValue({
              name: updated.name,
              displayTitle: updated.displayTitle,
              audience: updated.audience,
              startDate: updated.startDate,
              endDate: updated.endDate
            });
            this.updatedSurvey.emit(updated);
            this.markPendingChanges(false);
          },
          error: err => {
            this.snackbarService.openSnackBar('Error updating survey: ' + err.message);
          }
        });
    }
  }

  public publish(): void {
    if (this.survey.status !== SurveyStatus.DRAFT.toUpperCase()) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'Are you sure you want to publish this survey? After publishing, you will not be able to edit any of the questions if there is a submission.',
        buttonConfirmLabel: 'Publish Survey',
        buttonCancelLabel: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const updatedSurvey: UpdateSurveyRequest = {
          uuid: this.survey.uuid!,
          offeringUuid: this.survey.offeringUuid!,
          name: this.survey.name!,
          displayTitle: this.survey.displayTitle!,
          audience: this.survey.audience!,
          startDate: this.survey.startDate!,
          allowMultiSubmission: this.survey.allowMultiSubmission!,
          status: SurveyStatus.PUBLISHED,
          payload: this.survey.payload
        };
        this.surveyService.updateSurvey(updatedSurvey)
          .pipe(this.loadingService.withLoader())
          .subscribe({
            next: (published: Survey) => {
              this.survey = published;
              this.surveyForm.patchValue({
                name: published.name,
                displayTitle: published.displayTitle,
                audience: published.audience,
                startDate: published.startDate,
                endDate: published.endDate
              });
              this.updatedSurvey.emit(published);
            },
            error: err => {
              this.snackbarService.openSnackBar('Error publishing survey: ' + err.message);
            }
          });
      }
    });
  }

  onCancelChanges(): void {
    this.surveyForm.reset({
      name: this.survey.name,
      displayTitle: this.survey.displayTitle,
      audience: this.survey.audience,
      startDate: this.survey.startDate,
      endDate: this.survey.endDate
    });
    this.surveyForm.markAsPristine();
    this.markPendingChanges(false);
  }
}
